import type { Fact } from "../../common/fact-table";
import { type Bits, maskOf } from "./machine";

// The word read as an IEEE 754 float: the same bits the grid already holds, taken the way a language holding a
// double takes them. Nothing here is arithmetic — every function is a reading of a pattern, or a pattern read back
// out of what somebody typed, so the pad stays the integer machine it is and this is one more reading beside DEC.

export interface FloatFormat {
  bits: Bits;
  name: string;
  nickname: string;
  exponentBits: number;
  // The bits actually stored: a normal number's leading one is implied and is not one of them.
  significandBits: number;
  bias: number;
}

const BINARY64: FloatFormat = {
  bits: 64,
  name: "binary64",
  nickname: "double",
  exponentBits: 11,
  significandBits: 52,
  bias: 1023,
};

export const FLOAT_FORMATS: Partial<Record<Bits, FloatFormat>> = {
  16: { bits: 16, name: "binary16", nickname: "half", exponentBits: 5, significandBits: 10, bias: 15 },
  32: { bits: 32, name: "binary32", nickname: "single", exponentBits: 8, significandBits: 23, bias: 127 },
  64: BINARY64,
};

// Eight bits is the one word size the standard names no format for, so it is the one word with no float in it.
export function floatFormat(bits: Bits): FloatFormat | null {
  return FLOAT_FORMATS[bits] ?? null;
}

export type FloatKind = "zero" | "subnormal" | "normal" | "infinity" | "nan";

export interface FloatReading {
  format: FloatFormat;
  negative: boolean;
  kind: FloatKind;
  // The exponent field as it is stored, before the bias comes off it.
  biased: number;
  // The significand field as it is stored, without the leading one a normal number implies.
  stored: bigint;
  // Together these are the value exactly: significand × 2 to the exponent, leading one in and bias off.
  significand: bigint;
  exponent: number;
  // A double holds all three formats exactly, so this is the value itself rather than an approximation of it.
  value: number;
}

export function readFloat(pattern: bigint, format: FloatFormat): FloatReading {
  const bits = pattern & maskOf(format.bits);
  const negative = (bits >> BigInt(format.bits - 1)) === 1n;
  const biased = Number((bits >> BigInt(format.significandBits)) & maskOf(format.exponentBits));
  const stored = bits & maskOf(format.significandBits);
  const filled = (1 << format.exponentBits) - 1;

  const kind: FloatKind = biased === filled
    ? (stored === 0n ? "infinity" : "nan")
    : biased === 0
    ? (stored === 0n ? "zero" : "subnormal")
    : "normal";

  // A zero and a subnormal share the smallest exponent the format has, which is the one the bias is off by one for.
  const implied = biased === 0 ? 0n : 1n << BigInt(format.significandBits);
  const significand = stored | implied;
  const exponent = Math.max(biased, 1) - format.bias - format.significandBits;
  const value = kind === "nan"
    ? NaN
    : kind === "infinity"
    ? (negative ? -Infinity : Infinity)
    : (negative ? -1 : 1) * Number(significand) * 2 ** exponent;

  return { format, negative, kind, biased, stored, significand, exponent, value };
}

export type FloatField = "sign" | "exponent" | "significand";

// Which of the three fields a bit is in, so the grid is the word split the way the format reads it rather than a
// picture drawn beside it: one rendering of the bits, tinted by what each one is for.
export function floatField(index: number, format: FloatFormat): FloatField {
  if (index === format.bits - 1) return "sign";
  return index >= format.significandBits ? "exponent" : "significand";
}

export const FLOAT_FIELDS: FloatField[] = ["sign", "exponent", "significand"];

export const FIELD_NAMES: Record<FloatField, string> = {
  sign: "Sign",
  exponent: "Exponent",
  significand: "Significand",
};

// What the card says about the word, under the box that is already saying what the value is. A row with nothing in it
// is one the value has none of, and `FactTable` is what takes it off: an infinity has no exponent to report, and a
// number its own shortest spelling says exactly has no second spelling worth a line of its own.
export function floatFacts(reading: FloatReading): Fact[] {
  const { format, kind, negative, biased, stored } = reading;
  const special = kind === "infinity" || kind === "nan";
  const exact = exactDecimal(reading);

  return [
    { label: "Exact", value: exact === shortestDecimal(reading) ? "" : exact },
    { label: "Hex float", value: hexLiteral(reading) },
    { label: "Class", value: CLASS_NAMES[kind] ?? (quiet(reading) ? "Quiet NaN" : "Signalling NaN") },
    { label: "Sign", value: negative ? "1 (negative)" : "0 (positive)" },
    { label: "Exponent", value: special || kind === "zero" ? "" : String(Math.max(biased, 1) - format.bias) },
    { label: "Exponent field", value: String(biased) },
    // An infinity is the one value whose significand field says nothing: it is zero because the format says so.
    {
      label: kind === "nan" ? "Payload" : "Significand field",
      value: kind === "infinity" ? "" : `0x${stored.toString(16).toUpperCase()}`,
    },
    // The gap to the neighbouring value, which is the whole of what precision means at the size being looked at.
    { label: "Step", value: special ? "" : String(2 ** reading.exponent) },
  ];
}

const CLASS_NAMES: Partial<Record<FloatKind, string>> = {
  zero: "Zero",
  subnormal: "Subnormal",
  normal: "Normal",
  infinity: "Infinity",
};

// The top significand bit is what tells the two NaNs apart, and it is the only thing on this page that reads it.
function quiet(reading: FloatReading): boolean {
  return (reading.stored >> BigInt(reading.format.significandBits - 1)) === 1n;
}

// The shortest decimal that comes back to these same bits, which for the two narrow formats is not what a double
// prints: 0.1 as a binary32 is 0.1 to anything reading it as one, and 0.10000000149011612 to anything reading a double.
export function shortestDecimal(reading: FloatReading): string {
  if (reading.kind === "nan") return "NaN";
  if (reading.kind === "infinity") return reading.negative ? "-Infinity" : "Infinity";
  // A minus zero is a zero to `String`, and it is the one value on this page whose sign is the whole of the point.
  if (reading.kind === "zero") return reading.negative ? "-0" : "0";
  if (reading.format.bits === BINARY64.bits) return String(reading.value);

  const target = encodeValue(reading.value, reading.format);
  for (let digits = 1; digits <= DOUBLE_DIGITS; digits++) {
    const candidate = Number(reading.value.toPrecision(digits));
    if (encodeValue(candidate, reading.format) === target) return String(candidate);
  }
  return String(reading.value);
}

// Every finite binary float is a finite decimal, and saying which one is the whole reason a page like this exists:
// nothing else on screen says that the 0.1 somebody typed is stored as a number that is not 0.1.
export function exactDecimal(reading: FloatReading): string {
  if (reading.kind === "nan" || reading.kind === "infinity") return "";
  const sign = reading.negative ? "-" : "";
  if (reading.exponent >= 0) return sign + (reading.significand << BigInt(reading.exponent)).toString();

  // Dividing by a power of two is multiplying by the same power of five and moving the point that many places.
  const places = -reading.exponent;
  const digits = (reading.significand * 5n ** BigInt(places)).toString().padStart(places + 1, "0");
  const fraction = digits.slice(digits.length - places).replace(/0+$/, "");
  return sign + digits.slice(0, digits.length - places) + (fraction === "" ? "" : `.${fraction}`);
}

// C's own spelling, which is the one form of a float that is both exact and short enough to read at a glance.
export function hexLiteral(reading: FloatReading): string {
  const { format, kind, negative, biased, stored } = reading;
  const sign = negative ? "-" : "";
  if (kind === "nan") return "nan";
  if (kind === "infinity") return `${sign}inf`;
  if (kind === "zero") return `${sign}0x0p+0`;

  // The field is left-aligned onto whole hex digits first, binary16 and binary32 not being multiples of four wide.
  const width = Math.ceil(format.significandBits / 4);
  const fraction = (stored << BigInt(width * 4 - format.significandBits)).toString(16).toUpperCase()
    .padStart(width, "0").replace(/0+$/, "");
  const power = Math.max(biased, 1) - format.bias;
  return `${sign}0x${kind === "normal" ? 1 : 0}${fraction === "" ? "" : `.${fraction}`}p${
    power < 0 ? "" : "+"
  }${power}`;
}

// What the box beside the bits takes: a decimal, a power of ten, C's hexadecimal float, and the two words for the
// values no decimal spells. Null is a box nobody has finished filling in as much as it is one that is wrong.
export function parseFloatText(text: string, format: FloatFormat): bigint | null {
  const trimmed = text.trim().replace(/_/g, "");
  const negative = trimmed.startsWith("-");
  const body = /^[-+]/.test(trimmed) ? trimmed.slice(1) : trimmed;

  if (/^(?:inf|infinity)$/i.test(body)) return infinityOf(negative, format);
  if (/^nan$/i.test(body)) return quietNaN(format);

  const hex = /^0[xX]([0-9a-fA-F]*)(?:\.([0-9a-fA-F]*))?(?:[pP]([-+]?\d+))?$/.exec(body);
  if (hex) {
    const [, whole, fraction = "", power = "0"] = hex;
    if (whole === "" && fraction === "") return null;
    return scaled(negative, BigInt(`0x0${whole}${fraction}`), 2n, Number(power) - fraction.length * 4, format);
  }

  const decimal = /^(\d*)(?:\.(\d*))?(?:[eE]([-+]?\d+))?$/.exec(body);
  if (!decimal) return null;
  const [, whole, fraction = "", power = "0"] = decimal;
  if (whole === "" && fraction === "") return null;
  return scaled(negative, BigInt(`0${whole}${fraction}`), 10n, Number(power) - fraction.length, format);
}

// The next value the format can hold in that direction, which is the one place a bit grid cannot be walked to: the
// step across a binade carries into the exponent, and the step across zero changes the sign.
export function stepFloat(pattern: bigint, format: FloatFormat, up: boolean): bigint {
  const bits = pattern & maskOf(format.bits);
  const sign = 1n << BigInt(format.bits - 1);
  const magnitude = bits & (sign - 1n);
  const largest = maskOf(format.exponentBits) << BigInt(format.significandBits);
  if (magnitude > largest) return bits;

  // Below the sign bit the magnitudes run the same way on both halves of the line, so only the direction flips.
  if (((bits & sign) === sign) === up) {
    if (magnitude === 0n) return (up ? 0n : sign) | 1n;
    return (bits & sign) | (magnitude - 1n);
  }
  return magnitude === largest ? bits : (bits & sign) | (magnitude + 1n);
}

// Sixteen decimal digits either side of the point is more than any of the three formats can tell apart.
const DOUBLE_DIGITS = 17;

// Past these a literal is an answer either way, and nobody is owed a ten-thousand-digit multiplication to reach it.
const MAX_POWER = 1200;

const CONVERTER = new DataView(new ArrayBuffer(8));

// A value rounded into a format from binary64's own bits, which is exact for every double there is.
function encodeValue(value: number, format: FloatFormat): bigint {
  if (Number.isNaN(value)) return quietNaN(format);
  if (!Number.isFinite(value)) return infinityOf(value < 0, format);

  CONVERTER.setFloat64(0, value);
  const wide = readFloat(CONVERTER.getBigUint64(0), BINARY64);
  return scaled(wide.negative, wide.significand, 2n, wide.exponent, format);
}

// A literal as the exact fraction it is, which is what keeps the rounding a single one: a decimal read into a double
// first and into the format afterwards is rounded twice, and twice is once too many.
function scaled(negative: boolean, digits: bigint, radix: bigint, power: number, format: FloatFormat): bigint {
  if (digits === 0n) return signOf(negative, format);
  if (power > MAX_POWER) return infinityOf(negative, format);
  if (power < -MAX_POWER) return signOf(negative, format);

  const scale = radix ** BigInt(Math.abs(power));
  return power >= 0
    ? roundToFormat(negative, digits * scale, 1n, format)
    : roundToFormat(negative, digits, scale, format);
}

// Nearest, ties to even, which is the rounding every one of these formats is defined to do. The exponent is settled
// first — the one whose quotient is exactly as wide as the format holds, or the smallest exponent there is — and the
// remainder against the divisor is then the whole of the question the rounding asks.
function roundToFormat(negative: boolean, num: bigint, den: bigint, format: FloatFormat): bigint {
  if (num === 0n) return signOf(negative, format);

  const width = format.significandBits + 1;
  const least = 1 - format.bias - format.significandBits;
  let exponent = Math.max(least, bitLength(num) - bitLength(den) - width);
  let quotient = 0n;
  let remainder = 0n;
  let divisor = 0n;

  for (;;) {
    const numerator = exponent < 0 ? num << BigInt(-exponent) : num;
    divisor = exponent > 0 ? den << BigInt(exponent) : den;
    quotient = numerator / divisor;
    remainder = numerator % divisor;
    if (bitLength(quotient) > width) exponent += 1;
    else if (bitLength(quotient) < width && exponent > least) exponent -= 1;
    else break;
  }

  const twice = remainder * 2n;
  if (twice > divisor || (twice === divisor && (quotient & 1n) === 1n)) quotient += 1n;
  // Rounding up out of the binade is the one carry that reaches the exponent, and it lands on a power of two exactly.
  if (bitLength(quotient) > width) {
    quotient >>= 1n;
    exponent += 1;
  }

  // A quotient with the leading bit set is a normal, and the leading bit is the one the format does not store.
  const implied = 1n << BigInt(format.significandBits);
  const biased = quotient >= implied ? exponent + format.bias + format.significandBits : 0;
  if (biased >= (1 << format.exponentBits) - 1) return infinityOf(negative, format);
  return signOf(negative, format) | (BigInt(biased) << BigInt(format.significandBits))
    | (biased === 0 ? quotient : quotient - implied);
}

function bitLength(value: bigint): number {
  return value === 0n ? 0 : value.toString(2).length;
}

function signOf(negative: boolean, format: FloatFormat): bigint {
  return negative ? 1n << BigInt(format.bits - 1) : 0n;
}

function infinityOf(negative: boolean, format: FloatFormat): bigint {
  return signOf(negative, format) | (maskOf(format.exponentBits) << BigInt(format.significandBits));
}

// The quiet one, since the bit that says so is the one a hardware NaN arrives with and a signalling one is a choice.
function quietNaN(format: FloatFormat): bigint {
  return infinityOf(false, format) | (1n << BigInt(format.significandBits - 1));
}
